<template>
  <form @submit.prevent="handleSubmit">
    <div class="form-field">
      <input-text
        elem-id="soAdditionalServiceName"
        data-testid="so-additional-services-name-input"
        v-model="formData.so_additional_service_name"
        :label="t('Nome do Serviço Adicional')"
        :placeholder="t('Digite o nome do serviço adicional')"
        type="text"
        required
        :validation="validateName"
        fluid
      />
      <small v-if="errors.so_additional_service_name" class="error-message">{{ errors.so_additional_service_name }}</small>
    </div>
    <div class="form-field">
      <select-list
        elem-id="soAdditionalServiceCategoryId"
        data-testid="so-additional-services-category-select"
        v-model="formData.so_additional_service_category_id"
        :label="t('Categoria')"
        :itens-select="categoryOptions"
        :placeholder="t('Selecione uma categoria')"
        :auto-select-first="false"
      />
    </div>
    <div class="flex-container">
      <div class="flex-item-half form-field">
        <input-currency
          elem-id="soAdditionalServiceDefaultPrice"
          data-testid="so-additional-services-default-price-input"
          v-model="formData.so_additional_service_default_price"
          :label="t('Preço Padrão')"
          required
        />
      </div>
      <div class="flex-item-half form-field">
        <input-number
          elem-id="soAdditionalServiceAdditionalDeadlineDays"
          data-testid="so-additional-services-deadline-days-input"
          v-model="formData.so_additional_service_additional_deadline_days"
          :label="t('Prazo Adicional (dias)')"
          :placeholder="t('Dias adicionais')"
          :min="0"
        />
      </div>
    </div>
    <div class="form-field">
      <input-rich-text
        elem-id="soAdditionalServiceDescription"
        data-testid="so-additional-services-description-input"
        v-model="formData.so_additional_service_description"
        :label="t('Descrição')"
        :placeholder="t('Digite a descrição')"
      />
    </div>

    <!-- Production Steps Section -->
    <SOProductionStepsPicker
      v-if="props.isEditMode && itemIdNumber"
      ref="stepsPickerRef"
      :entityId="itemIdNumber"
      entityType="additional_service"
      elemIdPrefix="soAdditionalServiceStep"
    />

    <!-- Attributes Binding Section -->
    <SOAdditionalServiceAttributesPicker
      v-if="props.isEditMode && itemIdNumber"
      ref="attrPickerRef"
      :serviceId="itemIdNumber"
      elemIdPrefix="soAdditionalServiceAttr"
    />
  </form>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOCatalog } from '../../composables/useSOCatalog';
import SOProductionStepsPicker from '../so-production-steps/SOProductionStepsPicker.vue';
import SOAdditionalServiceAttributesPicker from './SOAdditionalServiceAttributesPicker.vue';
import { eventBus } from '../../eventBus';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface KVItem {
  value: Id;
  label: string;
}

interface SelectOption {
  ID: Id;
  Name: string;
}

interface AdditionalServiceData {
  so_additional_service_id?: Id;
  so_additional_service_name?: string;
  so_additional_service_category_id?: Id | null;
  so_additional_service_default_price?: number | null;
  so_additional_service_additional_deadline_days?: number | null;
  so_additional_service_description?: string;
}

interface StepsPickerInstance {
  saveSteps: () => Promise<void>;
}

interface AttrPickerInstance {
  saveAttributes: () => Promise<void>;
}

interface Props {
  itemId?: Id | null;
  isEditMode?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  itemId: null,
  isEditMode: false,
});

const emit = defineEmits<{
  'submit-success': [];
  'submit-error': [error: unknown];
}>();

const { t } = useI18n();

const { getById, save } = useSOCatalog('additional_services');
const { fetchKV: fetchCategoryKV } = useSOCatalog('service_categories');

const itemIdNumber = computed<number | null>(() => {
  if (!props.itemId) return null;
  return typeof props.itemId === 'string' ? parseInt(props.itemId, 10) : props.itemId;
});

const formData = reactive<{
  so_additional_service_name: string;
  so_additional_service_category_id: Id | null;
  so_additional_service_default_price: number | null;
  so_additional_service_additional_deadline_days: number | null;
  so_additional_service_description: string;
}>({
  so_additional_service_name: '',
  so_additional_service_category_id: null,
  so_additional_service_default_price: null,
  so_additional_service_additional_deadline_days: null,
  so_additional_service_description: ''
});

const errors = reactive<{ so_additional_service_name: string }>({
  so_additional_service_name: ''
});

const isLoading = ref<boolean>(false);
const categoryOptions = ref<SelectOption[]>([]);

const stepsPickerRef = ref<StepsPickerInstance | null>(null);
const attrPickerRef = ref<AttrPickerInstance | null>(null);

const validateName = (): boolean => {
  return !!formData.so_additional_service_name && formData.so_additional_service_name.trim() !== '' && formData.so_additional_service_name.length <= 255;
};

const loadCategoryOptions = async (): Promise<void> => {
  try {
    const kvData = await fetchCategoryKV() as KVItem[];
    categoryOptions.value = kvData.map(item => ({ ID: item.value, Name: item.label }));
  } catch (err) {
    logError('SOAdditionalServicesForm', 'loadCategoryOptions', 'Error', err);
    categoryOptions.value = [];
  }
};

const loadForEdit = async (): Promise<void> => {
  if (!itemIdNumber.value) return;
  log('SOAdditionalServicesForm', 'loadForEdit', 'Loading for edit', itemIdNumber.value);
  isLoading.value = true;
  try {
    const data = await getById(itemIdNumber.value) as AdditionalServiceData | null;
    if (!data) throw new Error('Dados nao encontrados');
    formData.so_additional_service_name = data.so_additional_service_name || '';
    formData.so_additional_service_category_id = data.so_additional_service_category_id || null;
    formData.so_additional_service_default_price = data.so_additional_service_default_price != null ? data.so_additional_service_default_price / 100 : null;
    formData.so_additional_service_additional_deadline_days = data.so_additional_service_additional_deadline_days || null;
    formData.so_additional_service_description = data.so_additional_service_description || '';
    clearErrors();
  } catch (error) {
    logError('SOAdditionalServicesForm', 'loadForEdit', 'Error', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao carregar serviço adicional') + ': ' + message);
    emit('submit-error', error);
  } finally {
    isLoading.value = false;
  }
};

const validateForm = (): boolean => {
  clearErrors();
  let isValid = true;
  if (!formData.so_additional_service_name || formData.so_additional_service_name.trim() === '') {
    errors.so_additional_service_name = t('Campo obrigatorio');
    isValid = false;
  }
  return isValid;
};

const clearErrors = (): void => { errors.so_additional_service_name = ''; };

const resetForm = (): void => {
  formData.so_additional_service_name = '';
  formData.so_additional_service_category_id = null;
  formData.so_additional_service_default_price = null;
  formData.so_additional_service_additional_deadline_days = null;
  formData.so_additional_service_description = '';
  clearErrors();
};

const handleSubmit = async (): Promise<void> => {
  if (!validateForm()) return;
  isLoading.value = true;
  try {
    const payload: AdditionalServiceData = {
      so_additional_service_name: formData.so_additional_service_name.trim(),
      so_additional_service_category_id: formData.so_additional_service_category_id,
      so_additional_service_default_price: formData.so_additional_service_default_price != null ? Math.round(formData.so_additional_service_default_price * 100) : 0,
      so_additional_service_additional_deadline_days: formData.so_additional_service_additional_deadline_days || null,
      so_additional_service_description: formData.so_additional_service_description || ''
    };
    if (props.isEditMode && itemIdNumber.value) {
      payload.so_additional_service_id = itemIdNumber.value;
      await save(payload);
      eventBus.emit('globalMessage.success', t('Serviço adicional atualizado com sucesso'));
    } else {
      await save(payload);
      eventBus.emit('globalMessage.success', t('Serviço adicional criado com sucesso'));
    }
    // Save production steps if in edit mode
    if (props.isEditMode && itemIdNumber.value && stepsPickerRef.value) {
      await stepsPickerRef.value.saveSteps();
    }
    // Save attribute bindings if in edit mode
    if (props.isEditMode && itemIdNumber.value && attrPickerRef.value) {
      await attrPickerRef.value.saveAttributes();
    }

    emit('submit-success');
    resetForm();
  } catch (error) {
    logError('SOAdditionalServicesForm', 'handleSubmit', 'Error', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao salvar serviço adicional') + ': ' + message);
    emit('submit-error', error);
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.itemId, async (newId) => {
  if (newId && props.isEditMode) { await nextTick(); await loadForEdit(); }
});

onMounted(() => {
  loadCategoryOptions();
  if (props.isEditMode && itemIdNumber.value) loadForEdit();
});

defineExpose({ loadForEdit, handleSubmit, resetForm, validateForm, getItemName: (): string => formData.so_additional_service_name });
</script>

<style scoped>
.form-field { display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 1rem; }
.flex-container { display: flex; gap: 1rem; }
.flex-item-half { flex: 1; }
.error-message { color: var(--p-red-500); font-size: 0.875rem; margin-top: 0.25rem; display: block; }
</style>
